import React from 'react';
import { Link2 as LinkIcon } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button';
import { Separator } from '@epoch-ui/components/Separator';
import { QuestionResponse } from 'types/question-response.types';
import { EventType, Session } from 'types/event.types';
import { GuestStatus } from 'types/guest.types';
import dayjs from 'utils/extendedDayJS';
import { SessionCard, SessionCardTitleAndDate } from 'components/cards/SessionCard';
import { cn } from '@epoch-ui/lib/utils';
import { RsvpSticker } from 'components/cards/components/RsvpSticker/RsvpSticker';

import { useGuestSideSheetContext } from '../../guestSideSheetContext';
import { formatResponse } from '../../utils';

export const PlusOneSideSheetContent = (): React.ReactNode => {
  const {
    guestData,
    selectedPlusOneGuest,
    setSelectedPlusOneId,
    selectedSession,
    allParentPlusOnes
  } = useGuestSideSheetContext();
  const plusOneGuest = selectedPlusOneGuest!;
  const { guest, event } = guestData;
  const isMultiSessionEvent = event.eventType === EventType.MULTI_SESSIONS;
  const plusOneConfig = isMultiSessionEvent ? selectedSession?.plusOneConfig : event.plusOneConfig;
  const hasQuestionnaire =
    plusOneConfig?.collectQuestionnaireResponses &&
    event.preEventQuestions &&
    event.preEventQuestions.length > 0 &&
    plusOneGuest.questionnaireResponses;
  const questionnaireResponseMap: Record<number, QuestionResponse> =
    plusOneGuest.questionnaireResponses.reduce(
      (acc, response) => ({
        ...acc,
        [response.id]: response
      }),
      {}
    );

  const ownerGuestName = `${guest.firstName} ${guest.lastName}`;

  const sessions = event.sessions || [];
  const parentPlusOne = allParentPlusOnes.find((po) => po.plusOneId === plusOneGuest.plusOneId);
  const plusOneSessionStatuses = parentPlusOne?.sessionStatus || [];

  const sessionStatusMap = plusOneSessionStatuses.reduce<{ [id: number]: GuestStatus }>(
    (acc, { eventId, status }) => ({ ...acc, [eventId]: status }),
    {}
  );

  const filteredSessions = sessions.filter((session) => session.id in sessionStatusMap);

  const eventTimezone = event.timezoneCode || 'UTC';
  const displayedTimezone = dayjs.tz.guess() || eventTimezone;

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      {/* Owner Guest Section */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <LinkIcon size={16} strokeWidth={2.5} />
        <span className="tw-text-sm tw-font-medium tw-leading-4 tw-text-black">
          +1 guest of{' '}
          <Button
            variant="link"
            size="xxs"
            className="tw-h-auto tw-p-0"
            onClick={() => setSelectedPlusOneId(null)}
          >
            {ownerGuestName}
          </Button>
        </span>
      </div>

      {/* Sessions Section */}
      {isMultiSessionEvent && filteredSessions.length > 0 && (
        <>
          <Separator />
          <div className="tw-space-y-4">
            <div className="tw-text-sm tw-font-medium tw-leading-5">Sessions</div>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {filteredSessions.map((session: Session) => (
                <SessionCard
                  className={cn(
                    'tw-cursor-default tw-rounded-xl',
                    session.id === selectedSession?.id ? '' : 'tw-opacity-40'
                  )}
                  key={session.id}
                  session={session}
                  eventTimezone={eventTimezone}
                  displayedTimezone={displayedTimezone}
                >
                  <SessionCardTitleAndDate />
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <RsvpSticker
                      className="tw-rounded-xl tw-p-2"
                      rsvpStatus={sessionStatusMap[session.id]}
                    />
                  </div>
                </SessionCard>
              ))}
            </div>
          </div>
        </>
      )}

      <Separator />

      {/* Guest Info Section */}
      <div className="tw-text-black">
        <span className="tw-text-base tw-font-medium tw-leading-5">Guest info</span>
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
          {/* First Name / Last Name Row */}
          <div className="tw-flex tw-gap-4">
            <div className="tw-flex-1">
              <div className="tw-mb-1 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                First name
              </div>
              <div className="tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
                {plusOneGuest.firstName}
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-mb-1 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                Last name
              </div>
              <div className="tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
                {plusOneGuest.lastName}
              </div>
            </div>
          </div>

          {/* Questionnaire Responses */}
          {hasQuestionnaire &&
            event.preEventQuestions?.map((question) => {
              const questionResponse = questionnaireResponseMap[question.id];
              const response = questionResponse ? formatResponse(questionResponse) : 'N/A';

              return (
                <div key={question.id}>
                  <div className="tw-mb-1 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                    {question.label}
                  </div>
                  <div className="tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
                    {response || 'N/A'}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <Separator />
    </div>
  );
};
